import { defaultAdultAge } from "@/helpers/defaultAdultAge.ts";
import { RatePlanMerged } from "@/helpers/interfaces/RatePlanMerged.ts";
import { mergeRatePlans } from "@/helpers/mergeRatePlans.ts";
import { PersonType, ReservationOfferAccommodation } from "@/helpers/types/types.ts";
import { EngineStoreData } from "@/stores/EngineStore/EngineStore.ts";
import { AdultType } from "@/stores/EngineStore/interfaces/AdultType.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import { ChildType } from "@/stores/EngineStore/interfaces/ChildType.ts";

const convertReservationToCartItem = (
  reservation: ReservationOfferAccommodation,
  state: EngineStoreData,
): CartAccommodationItem => {
  const adults: AdultType[] = [];
  const children: ChildType[] = [];

  const rateGroup = state.availableRateGroups.find((rg) =>
    rg.ratePlans.find((rp) => rp.id === reservation.ratePlan?.id),
  );

  if (!rateGroup) {
    throw new Error(`Rate group not found for rate plan: ${reservation.ratePlan?.id}`);
  }

  reservation.guests.forEach((guest) => {
    if (guest.personType === PersonType.Adult) {
      adults.push({ id: guest.id, type: PersonType.Adult, age: defaultAdultAge });
    } else {
      children.push({ id: guest.id, type: PersonType.Child, age: guest.age || 1 });
    }
  });

  // Find the correct parent rate plan from merged rate plans
  const mergedRatePlans = mergeRatePlans(rateGroup);

  // Find if the reservation rate plan is a parent or child
  let ratePlan: RatePlanMerged | undefined;

  // Check if it's a parent rate plan
  ratePlan = mergedRatePlans.find((rp) => rp.id === reservation.ratePlan?.id);

  // If not found as parent, find the parent that contains this rate plan as child
  if (!ratePlan) {
    const parentRatePlan = mergedRatePlans.find(
      (rp) => rp.childRatePlans?.some((crp) => crp.id === reservation.ratePlan?.id),
    );
    if (parentRatePlan) {
      ratePlan = parentRatePlan;
    }
  }

  if (!ratePlan) {
    throw new Error(`Rate plan not found in merged structure: ${reservation.ratePlan?.id}`);
  }

  return {
    id: reservation.id,
    mandatoryExtras: [],
    adults,
    children,
    optionalExtras: [],
    dateRange: [new Date(reservation.checkIn), new Date(reservation.checkOut)],
    ratePlan,
    rateGroup,
  };
};

/**
 * Used to set full cart accommodation items from reservation offer accommodations
 */
export const addFullCartAccommodationItemsHelper = (
  state: EngineStoreData,
  accommodations: ReservationOfferAccommodation[],
) => {
  const cartAccommodationItems: CartAccommodationItem[] = accommodations.map((acc) =>
    convertReservationToCartItem(acc, state),
  );

  // must set defaultDateRange from first accommodation (since all accommodations must have the same date range)
  const newDefaultDateRange = cartAccommodationItems[0].dateRange;

  return { ...state, defaultDateRange: newDefaultDateRange, cartAccommodationItems };
};
